import React from 'react';
import * as styles from 'common/components/header/Header.scss';
import { tl } from 'common/services/i18nService';
import { Confirm, Icon } from 'semantic-ui-react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { getCommitteeById } from '../../../../services/committeeService';
import { getExternalSystemName, getTransferActions, SyncClickActions } from '../../services/externalSystemService';
import { onSyncActionClick } from '../../../../store/syncButton/actions';
import { RootState } from '../../../../store/rootReducer';
import { ModalConfig } from './ExternalInfoButton';
import { SyncActions } from './SyncActions';
import { CommitteeType } from '../../../../store/assessment/constants';
import {
    ActionsFromBackendPermission,
    currentActionForPlannerIsVisible,
} from '../../../../services/optionsMenuItemsService';
import { ExternalSystem } from '../../../../store/common/constants';
import { getPartyCommitteeIdForContext } from '../../../../services/gradingPartyService';

interface ComponentProps {
    setModalConfig: (props: ModalConfig) => void;
    modalConfig: ModalConfig;
    initialState: ModalConfig;
}
export enum TransferOptions {
    allGraders = 'AllGraders',
    toCommittee = 'toCommittee',
}

export const GradesTransfer: React.FC<ComponentProps> = React.memo(({
    setModalConfig, modalConfig, initialState,
}) => {
    const dispatch = useDispatch();
    const { context, assessment } = useSelector((state: RootState) => state);
    const {
        isTransferringGrades, gradeTransferTimestamp, hasGradesToTransfer, externalSystem, committees,
    } = assessment;
    const selectedCommitteeId = getPartyCommitteeIdForContext(context.partyState.gradingPartyDocument);
    const committee = getCommitteeById(selectedCommitteeId);
    const externalSystemText = getExternalSystemName(externalSystem as ExternalSystem);
    const transferClick = (name: string) => () => {
        setModalConfig({
            ...modalConfig,
            title:
                name === TransferOptions.allGraders
                    ? tl('grading.header.syncTransfer.AllGraders')
                    : tl('grading.header.syncTransfer.transferFor', {
                        committeeName: committee.name,
                    }),
            isOpen: true,
            currentTransfer: name,
        });
    };
    const onCancel = () => {
        setModalConfig(initialState);
    };

    const onAccept = () => {
        if (modalConfig.currentTransfer === TransferOptions.allGraders) {
            dispatch(onSyncActionClick(
                SyncClickActions.TRANSFER_GRADES_TO_EXTERNAL_SYSTEM
            ));
        } else {
            dispatch(onSyncActionClick(
                SyncClickActions.TRANSFER_GRADES_FROM_COMMITTEE
            ));
        }

        setModalConfig(initialState);
    };

    const getCommitteeRightSide = () => {
        if (committee.gradeTransferTimestamp) {
            const lastUpdate = moment(Number(committee.gradeTransferTimestamp));
            return lastUpdate.isValid() && (
                <React.Fragment>
                    <Icon name="check circle" color="green" />
                    {`${tl('grading.header.syncTransfer.lastTransfer')} ${lastUpdate.fromNow()} `}
                </React.Fragment>
            );
        }

        if (committee.gradingCompleted) {
            return (
                <React.Fragment>
                    <Icon name="dot circle" color="blue" />
                    {tl('grading.header.syncTransfer.readyToTransfer')}
                </React.Fragment>
            );
        }

        return tl('grading.header.syncTransfer.pendingConfirm');
    };

    const areAllCommitteesGradesTransferred = () => {
        const filteredCommittees = committees ? committees.filter((item: any) => {
            return item.type === CommitteeType.grading;
        }) : [];
        return filteredCommittees.length > 0 && filteredCommittees.every((item: any) => {
            return !item.hasGradesToTransfer;
        });
    };

    const getAllGradersRightSide = () => {
        if (gradeTransferTimestamp && areAllCommitteesGradesTransferred()) {
            const lastUpdate = moment(Number(gradeTransferTimestamp));
            return lastUpdate.isValid() && (
                <React.Fragment>
                    <Icon name="check circle" color="green" />
                    {`${tl('grading.header.syncTransfer.lastTransfer')} ${lastUpdate.fromNow()
                    } `}
                </React.Fragment>
            );
        }

        if (hasGradesToTransfer) {
            return (
                <React.Fragment>
                    <Icon name="dot circle" color="blue" />
                    {tl('grading.header.syncTransfer.readyToTransfer')}
                </React.Fragment>
            );
        }

        return tl('grading.header.syncTransfer.pendingConfirm');
    };

    const transferOptions = [];

    if (currentActionForPlannerIsVisible(ActionsFromBackendPermission.TRANSFER_GRADES)) {
        transferOptions.push({
            actionName: TransferOptions.allGraders,
            onClick: transferClick(TransferOptions.allGraders),
            leftSide: (
                <React.Fragment>
                    {isTransferringGrades ? (
                        <Icon name="circle notch" loading />
                    ) : null}
                    {tl('grading.header.syncTransfer.AllGraders')}
                </React.Fragment>
            ),
            rightSide: getAllGradersRightSide(),
            disabled: !hasGradesToTransfer,
        });
    }

    if (committee
        && currentActionForPlannerIsVisible(ActionsFromBackendPermission.TRANSFER_GRADES_COMMITTEE)
        && committee.type !== 'REASSESSMENT') {
        transferOptions.push({
            actionName: TransferOptions.toCommittee,
            onClick: transferClick(TransferOptions.toCommittee),
            leftSide: (
                <React.Fragment>
                    {isTransferringGrades ? (
                        <Icon name="circle notch" loading />
                    ) : null}
                    {tl('grading.header.syncTransfer.transferFor', {
                        committeeName: committee.name,
                    })}
                </React.Fragment>
            ),
            rightSide: getCommitteeRightSide(),
            disabled: !committee.hasGradesToTransfer
                || !committee.gradingCompleted
                || isTransferringGrades,
        });
    }

    return (
        <React.Fragment>
            <div className={styles.syncPopUpHeader}>
                <strong>
                    {tl('grading.header.transferGradesToExternalSystem', { externalSystem: externalSystemText })}
                </strong>
            </div>
            {transferOptions.length > 0 && <SyncActions actionData={transferOptions} bothSideOptions />}
            <SyncActions actionData={getTransferActions()} />
            <Confirm
                header={modalConfig.title}
                open={modalConfig.isOpen}
                content={tl('grading.header.syncTransfer.confirmationTransfer')}
                cancelButton={tl('modal.genericSimpleConfirmationModal.cancel')}
                confirmButton={tl('modal.genericSimpleConfirmationModal.confirm')}
                onCancel={onCancel}
                onConfirm={onAccept}
            />
        </React.Fragment>
    );
});
